/**
 * TechnologyVersionDialog Component
 *
 * Create/edit form for a `TechnologyVersion` record (issue #1283, part of
 * #1282). Mirrors `CreateSupplierDialog.tsx`'s form pattern (Forge dialog +
 * `TextField`/`SelectField` + react-hook-form/zod) rather than
 * `EditMilestoneDialog.tsx`'s native `<input>`/`<select>` pattern, per
 * CLAUDE.md's mandatory Forge-web-component rule.
 */

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  createZodResolver,
  createTechnologyVersionSchema,
  type CreateTechnologyVersionFormData,
} from '../forms/validation';
import { TextField, SelectField } from '../forms';
import {
  createTechnologyVersion,
  updateTechnologyVersion,
  ApiError,
  type TechnologyVersionStatusInput,
} from '../../lib/apiClient';
import type { TechnologyVersion } from '../../graphql/types';
import '@tylertech/forge/dialog';
import '@tylertech/forge/button';
import '@tylertech/forge/banner';
import './TechnologyVersionDialog.css';

export interface TechnologyVersionDialogProps {
  open: boolean;
  technologyId: string;
  /** Version being edited; omit (or pass `null`) to create a new one. */
  version?: TechnologyVersion | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const STATUS_OPTIONS: { value: TechnologyVersionStatusInput; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
  { value: 'deprecated', label: 'Deprecated' },
];

const DialogElement = 'forge-dialog' as React.ElementType;
const ForgeButton = 'forge-button' as React.ElementType;
const BannerElement = 'forge-banner' as React.ElementType;

export function TechnologyVersionDialog({
  open,
  technologyId,
  version = null,
  onSuccess,
  onCancel,
}: TechnologyVersionDialogProps): JSX.Element | null {
  const mode: 'create' | 'edit' = version ? 'edit' : 'create';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<CreateTechnologyVersionFormData>({
    resolver: createZodResolver(createTechnologyVersionSchema),
  });

  const statusValue = watch('status');

  useEffect(() => {
    if (open) {
      reset({
        versionLabel: version?.versionLabel ?? '',
        status: (version?.status as TechnologyVersionStatusInput) ?? 'draft',
        releaseDate: version?.releaseDate ?? '',
        notes: version?.notes ?? '',
      });
      setError(null);
    }
  }, [open, version, reset]);

  const handleClose = (): void => {
    if (isSubmitting) return;
    reset();
    setError(null);
    onCancel();
  };

  const onSubmit = async (data: CreateTechnologyVersionFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);
    try {
      const payload = {
        versionLabel: data.versionLabel,
        status: data.status,
        releaseDate: data.releaseDate || undefined,
        notes: data.notes || undefined,
      };
      if (mode === 'edit' && version) {
        await updateTechnologyVersion(technologyId, version.entityId, payload);
      } else {
        await createTechnologyVersion(technologyId, payload);
      }
      reset();
      onSuccess();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : `Failed to ${mode} version`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <DialogElement open={open} onDismiss={handleClose} aria-labelledby="technology-version-title">
      <div className="technology-version-dialog">
        <h2
          id="technology-version-title"
          className="forge-typography--headline6 technology-version-dialog__title"
        >
          {mode === 'create' ? 'Add Version' : 'Edit Version'}
        </h2>

        {error && (
          <BannerElement theme="error" dismissed={false} role="alert">
            <span>{error}</span>
          </BannerElement>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          <TextField
            label="Version Label"
            required
            error={errors.versionLabel}
            {...register('versionLabel')}
          />

          <SelectField
            label="Status"
            options={STATUS_OPTIONS}
            error={errors.status}
            value={statusValue ?? 'draft'}
            {...register('status')}
          />

          <TextField
            label="Release Date"
            type="date"
            error={errors.releaseDate}
            {...register('releaseDate')}
          />

          <TextField label="Notes" error={errors.notes} {...register('notes')} />

          <div className="technology-version-dialog__actions">
            <ForgeButton
              type="button"
              variant="outlined"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </ForgeButton>
            <ForgeButton type="submit" variant="raised" disabled={isSubmitting}>
              {isSubmitting
                ? mode === 'create'
                  ? 'Creating...'
                  : 'Saving...'
                : mode === 'create'
                  ? 'Create Version'
                  : 'Save'}
            </ForgeButton>
          </div>
        </form>
      </div>
    </DialogElement>
  );
}
